"use client";

import cn from "classnames";

import { PhoneIcon } from "@/components/icons/phone.icon";
import { Link, type LinkProps } from "@/components/link/link.component";

import { toTelHref } from "@/utils/string.util";
import { resolveLinkHref } from "@/utils/url.util";

import { brown } from "@/app/font";
import { useCampaign } from "@/contexts/campaignProvider.context";
import { useSettings } from "@/contexts/settingsProvider.context";

/** Link styled as a button. Supports link and phone CTAs, plus an optional color theme. */
export function ButtonLink({
  cta,
  href,
  label,
  phone,
  openInNewTab,
  theme,
  className,
  children,
  ...rest
}: ButtonLinkProps) {
  const settings = useSettings();
  const campaign = useCampaign();
  const resolved = cta
    ? resolveCtaLink(cta)
    : href && label
      ? {
          href,
          label,
          phone: phone ?? href.startsWith("tel:"),
          openInNewTab: openInNewTab ?? false,
        }
      : null;

  if (!resolved) return null;

  const buttonClassName = cn(
    brown.className,
    "button cursor-pointer items-center justify-center gap-2 rounded-full border px-6 py-2.5 font-bold uppercase duration-300 disabled:pointer-events-none disabled:select-none md:text-[18px]",
    "border-secondary text-secondary disabled:border-greyscale-strength disabled:text-greyscale-strength focus-visible:ring-secondary border-2 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:bg-transparent",
    className,
  );

  if (resolved.phone) {
    const desktopPhoneNumber =
      campaign?.tfnDesktop ?? settings?.phoneNumber ?? resolved.label;
    const mobilePhoneNumber =
      campaign?.tfnMobile ?? settings?.phoneNumber ?? resolved.label;

    return (
      <>
        {mobilePhoneNumber ? (
          <Link
            href={toTelHref(mobilePhoneNumber)}
            aria-label={`Call ${mobilePhoneNumber}`}
            className={cn(buttonClassName, "flex", "lg:hidden")}
            {...rest}>
            <PhoneIcon
              width={24}
              height={24}
              aria-hidden="true"
              className="fill-secondary"
            />
            {children ?? mobilePhoneNumber}
          </Link>
        ) : null}
        {desktopPhoneNumber ? (
          <Link
            href={toTelHref(desktopPhoneNumber)}
            aria-label={`Call ${desktopPhoneNumber}`}
            className={cn(buttonClassName, "hidden", "lg:flex")}
            {...rest}>
            <PhoneIcon
              width={24}
              height={24}
              aria-hidden="true"
              className="fill-secondary"
            />
            {children ?? desktopPhoneNumber}
          </Link>
        ) : null}
      </>
    );
  }

  return (
    <Link
      href={resolved.href}
      target={resolved.openInNewTab ? "_blank" : undefined}
      rel={resolved.openInNewTab ? "noopener noreferrer" : undefined}
      className={cn(
        brown.className,
        "button flex cursor-pointer items-center justify-center gap-2 rounded-full border px-6 py-2.5 font-bold uppercase duration-300 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:pointer-events-none disabled:select-none md:text-[18px]",
        theme ? buttonThemeClassName(theme) : DEFAULT_THEME,
        className,
      )}
      {...rest}>
      {children ?? resolved.label}
    </Link>
  );
}

export type CtaLinkFields = {
  ctaType?: string | null;
  ctaLabel?: string | null;
  phoneNumber?: string | null;
  ctaLink?: {
    url?: string | null;
    queryString?: string | null;
    linkType?: string | null;
    anchorId?: string | null;
    openInNewTab?: boolean | null;
  } | null;
};

export type ResolvedCtaLink = {
  href: string;
  label: string;
  phone: boolean;
  openInNewTab: boolean;
};

export type ButtonTheme =
  | "primary"
  | "secondary"
  | "tertiary"
  | "light"
  | "white"
  | "success"
  | "error";

export type ButtonLinkProps = Omit<LinkProps, "href" | "children"> & {
  cta?: CtaLinkFields;
  href?: string;
  label?: string;
  phone?: boolean;
  openInNewTab?: boolean;
  theme?: ButtonTheme | null;
  children?: React.ReactNode;
};

const DEFAULT_THEME =
  "border-secondary text-secondary disabled:border-greyscale-strength disabled:text-greyscale-strength focus-visible:ring-secondary border-2 disabled:bg-transparent";

const buttonThemeClassName = (theme: ButtonTheme) => {
  return cn({
    "border-transparent bg-primary text-secondary hover:bg-primary-400 focus-visible:ring-secondary":
      theme === "primary",
    "border-transparent bg-secondary text-white hover:bg-secondary-300 focus-visible:ring-secondary":
      theme === "secondary",
    "border-transparent bg-primary-100 text-secondary focus-visible:ring-secondary":
      theme === "tertiary",
    "border-transparent bg-greyscale-background text-greyscale-body focus-visible:ring-secondary":
      theme === "light",
    "border-greyscale-strength bg-white text-greyscale-body focus-visible:ring-secondary":
      theme === "white",
    "border-transparent bg-success text-white focus-visible:ring-success":
      theme === "success",
    "border-transparent bg-error text-white focus-visible:ring-error":
      theme === "error",
  });
};

export function resolveCtaLink(fields: CtaLinkFields): ResolvedCtaLink | null {
  if (fields.ctaType === "phone") {
    const label = fields.phoneNumber?.trim();
    const href = toTelHref(fields.phoneNumber);
    if (!label || !href) return null;
    return { href, label, phone: true, openInNewTab: false };
  }

  const label = fields.ctaLabel?.trim();
  const href = resolveLinkHref(fields.ctaLink);
  if (!label || !href) return null;

  return {
    href,
    label,
    phone: false,
    openInNewTab: fields.ctaLink?.openInNewTab ?? false,
  };
}
